import { NavLink, useNavigate } from 'react-router-dom';
import React, { useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
  FormErrorMessage,
  Select,
} from '@chakra-ui/react';

import CenteredAuth from 'layouts/auth/variants/Centered';

// Assets
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { authRepository } from 'api/authRepository';

import { z } from 'zod';
import { signUpSchema, flattenZodErrors, SignUpPayload } from 'validations/Validations';

function SignUp() {
  const toast = useToast();
  const navigate = useNavigate();

  // Chakra color mode
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'gray.400';
  const textColorDetails = useColorModeValue('navy.700', 'secondaryGray.600');
  const textColorBrand = useColorModeValue('brand.500', 'white');
  const brandStars = useColorModeValue('brand.500', 'brand.400');

  const [show, setShow] = React.useState(false);
  const handleClick = () => setShow(!show);

  const [formData, setFormData] = useState<SignUpPayload>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    userRole: '',
    contactNumber: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof SignUpPayload, string>>>({});

  const handleFieldChange = (field: keyof SignUpPayload, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    const fieldSchema = signUpSchema.shape[field];
    if (fieldSchema) {
      const result = fieldSchema.safeParse(value);
      setErrors((prev) => ({
        ...prev,
        [field]: result.success ? '' : result.error.issues[0].message,
      }));
    }
  };

  const handleSubmit = async () => {
    const result = signUpSchema.safeParse(formData);

    if (!result.success) {
      setErrors(flattenZodErrors(result.error));
      toast({
        title: 'Validation Error',
        description: 'Please fix the highlighted errors.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    try {
      const response = await authRepository.register(result.data);
      navigate('/auth/verification');
      toast({
        title: 'Registration Successful',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } catch (err) {
      toast({
        title: 'Registration Failed',
        description: 'Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  return (
    <CenteredAuth cardTop={{ base: '140px', md: '14vh' }} cardBottom={{ base: '50px', lg: '100px' }}>
      <Flex maxW="max-content" mx={{ base: 'auto', lg: '0px' }} me="auto" justifyContent="center" px={{ base: '20px', md: '0px' }} flexDirection="column">
        <Box me="auto">
          <Heading color={textColor} fontSize={{ base: '34px', lg: '36px' }} mb="10px">
            Sign Up
          </Heading>
          <Text mb="36px" ms="4px" color={textColorSecondary} fontWeight="400" fontSize="md">
            Enter your details to create an account!
          </Text>
        </Box>

        <Flex zIndex="2" direction="column" w={{ base: '100%', md: '420px' }} maxW="100%" background="transparent" borderRadius="15px" mx={{ base: 'auto', lg: 'unset' }} me="auto" mb={{ base: '20px', md: 'auto' }}>
          <FormControl isInvalid={!!errors.firstName}>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={{ sm: '10px', md: '26px' }}>
              <Flex direction="column">
                <FormLabel display="flex" ms="4px" fontSize="sm" fontWeight="500" color={textColor} mb="8px">
                  First name<Text color={brandStars}>*</Text>
                </FormLabel>
                <Input
                  isRequired
                  fontSize="sm"
                  placeholder="First name"
                  variant="auth"
                  mb="8px"
                  size="lg"
                  value={formData.firstName}
                  onChange={(e) => handleFieldChange('firstName', e.target.value)}
                />
                {errors.firstName && <FormErrorMessage mb="16px">{errors.firstName}</FormErrorMessage>}
              </Flex>

              <Flex direction="column">
                <FormLabel display="flex" ms="4px" fontSize="sm" fontWeight="500" color={textColor} mb="8px">
                  Last name<Text color={brandStars}>*</Text>
                </FormLabel>
                <Input
                  isRequired
                  variant="auth"
                  fontSize="sm"
                  placeholder="Last name"
                  mb="8px"
                  size="lg"
                  value={formData.lastName}
                  onChange={(e) => handleFieldChange('lastName', e.target.value)}
                />
                {errors.lastName && <FormErrorMessage mb="16px">{errors.lastName}</FormErrorMessage>}
              </Flex>
            </SimpleGrid>
          </FormControl>

          {/* Email */}
          <FormControl isInvalid={!!errors.email}>
            <FormLabel display="flex" ms="4px" fontSize="sm" fontWeight="500" color={textColor} mb="8px">
              Email<Text color={brandStars}>*</Text>
            </FormLabel>
            <Input
              isRequired
              variant="auth"
              fontSize="sm"
              type="email"
              placeholder="mail@simmmple.com"
              mb="8px"
              size="lg"
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              onBlur={(e) => handleFieldChange('email', e.target.value)}
            />
            {errors.email && <FormErrorMessage mb="16px">{errors.email}</FormErrorMessage>}
          </FormControl>

          {/* Role Dropdown */}
          <FormControl isInvalid={!!errors.userRole}>
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              mb="8px"
            >
              Role<Text color={brandStars}>*</Text>
            </FormLabel>
            <Select
              isRequired
              variant="auth"
              fontSize="sm"
              placeholder="Select your role"
              mb="8px"
              size="lg"
              value={formData.userRole}
              onChange={(e) => handleFieldChange("userRole", e.target.value)}
            >
              <option value="Admin">Admin</option>
              <option value="OrgAdmin">OrgAdmin</option>
              <option value="DeptUser">DeptUser</option>
               <option value="Vendor">Vendor</option>
            </Select>
            {errors.userRole && <FormErrorMessage mb="16px">{errors.userRole}</FormErrorMessage>}
          </FormControl>


          {/* Password */}
          <FormControl isInvalid={!!errors.password}>
            <FormLabel ms="4px" fontSize="sm" fontWeight="500" color={textColor} display="flex">
              Password<Text color={brandStars}>*</Text>
            </FormLabel>
            <InputGroup size="md">
              <Input
                isRequired
                variant="auth"
                fontSize="sm"
                placeholder="Min. 8 characters"
                mb="8px"
                size="lg"
                type={show ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleFieldChange('password', e.target.value)}

              />
              <InputRightElement display="flex" alignItems="center" mt="4px">
                <Icon
                  color={textColorSecondary}
                  _hover={{ cursor: 'pointer' }}
                  as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                  onClick={handleClick}
                />
              </InputRightElement>
            </InputGroup>
            {errors.password && <FormErrorMessage mb="16px">{errors.password}</FormErrorMessage>}
          </FormControl>

          <Flex justifyContent="space-between" align="center" mb="24px">
            <FormControl display="flex" alignItems="start">
              <Checkbox id="remember-login" colorScheme="brand" me="10px" mt="3px" />
              <FormLabel htmlFor="remember-login" mb="0" fontWeight="normal" color={textColor} fontSize="sm">
                By creating an account you agree to the{' '}
                <Link href="https://simmmple.com/terms-of-service" fontWeight="500">
                  Terms and Conditions
                </Link>{' '}
                and{' '}
                <Link href="https://simmmple.com/privacy-policy" fontWeight="500">
                  Privacy Policy
                </Link>
              </FormLabel>
            </FormControl>
          </Flex>

          <Button variant="brand" fontSize="14px" fontWeight="500" w="100%" h="50" mb="24px" onClick={handleSubmit}>
            Create my account
          </Button>

          <Flex flexDirection="column" justifyContent="center" alignItems="start" maxW="100%" mt="0px">
            <Text color={textColorDetails} fontWeight="400" fontSize="sm">
              Already a member?
              <NavLink to="/auth/sign-in">
                <Text color={textColorBrand} as="span" ms="5px" fontWeight="500">
                  Sign in
                </Text>
              </NavLink>
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </CenteredAuth>
  );
}

export default SignUp;
